import { useGetTransactionsOfAccountTPBankListExportQuery } from 'services/transactionIDBanks/queries/useGetTransactionsOfAccountTPBankListExportQuery';
import toast from 'react-hot-toast';
import { isRequestError } from 'utils/requestError';
import { saveListExcel } from 'utils/tableExport/saveListExcel';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, InputDateTime, InputText, TableListData } from 'components';
import { Button, InputWraper, useForm, IUseFormStructureItem } from 'modules';
import React, { useCallback, useState } from 'react';
import type { TpbInqueryStatementList } from 'services/openapi';

import {
  columnsDefined,
  convertVariables,
  type FilterType,
  getInitFilterValues,
} from './config';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useGetTransactionsOfAccountTPBankQuery } from 'services/transactionIDBanks/queries';
import { checkValidActionRoleAdmin } from 'utils/helpers';
import { Actions } from 'configs/actions';

const AccountTPBankFeatures = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        // refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'accountNumber',
      label: 'Tiểu khoản',
      defaultValue: initFilterValues?.accountNumber,
      validate: Yup.string().required('Vui lòng nhập Tiểu khoản'),
    },
    {
      name: 'fromTime',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.fromTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.toTime).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.toTime);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'toTime',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.toTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.fromTime).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.fromTime);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const { isFetching: isLoadingExport, refetch: refetchExport } =
    useGetTransactionsOfAccountTPBankListExportQuery({
      ...convertVariables(variables),
      accountNumber: variables.accountNumber || '',
      page: '1',
      pageSize: String(
        Number((dataTable as unknown as { total?: number })?.total ?? 0),
      ),
    });

  const handleExportExcel = async () => {
    if (isLoadingExport) return;
    const total = (dataTable as unknown as { total?: number })?.total;
    if (total == null) {
      toast.error('Response thiếu total, không thể xuất đầy đủ danh sách');
      return;
    }
    try {
      const result = await refetchExport({ throwOnError: true });
      await saveListExcel(
        result.data?.data,
        columnsDefined as ColumnDef<TpbInqueryStatementList>[],
        'Lich_su_giao_dich_TPBank',
        true,
      );
    } catch (error) {
      if (!isRequestError(error)) {
        toast.error(
          error instanceof Error ? error.message : 'Không thể tải danh sách',
        );
      }
    }
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('accountNumber')}
                renderInput={InputText}
              />
            </div>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromTime')}
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toTime')}
                renderInput={InputDateTime}
              />
            </div>
          </div>
          {checkValidActionRoleAdmin(Actions.TPBank_InqueryTransaction) && (
            <div className='textCenter mt16 wrapButtonFilter'>
              <Button label={'Truy vấn'} type='submit' buttonType='success' />
              <Button
                className='ml15'
                onClick={handleExportExcel}
                label={'Tải về DS'}
                type='button'
                buttonType='info'
                isLoading={isLoadingExport}
              />
            </div>
          )}
        </form>
      </Card>

      <TableListData<TpbInqueryStatementList>
        columns={columnsDefined as ColumnDef<TpbInqueryStatementList>[]}
        useQuery={useGetTransactionsOfAccountTPBankQuery}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        callBackData={setDataTable}
        orderColumn
      />
    </>
  );
};

export default AccountTPBankFeatures;
